import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtINR, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ProjectDialog from "@/components/ProjectDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { PencilSimple, MapPin, Clock, Package, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/projects/${id}`).then(r => setP(r.data));
  useEffect(() => { load(); }, [id]);
  if (!p) return <div className="p-10 text-white/40">Loading…</div>;

  const onDelete = async () => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      nav("/projects");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <>
      <PageHeader
        title={p.name}
        subtitle={`${p.client?.company || p.client?.name || "—"} · ${p.location || ""}`}
        actions={
          <div className="flex items-center gap-2">
            <button data-testid="edit-project-btn" onClick={() => setOpen(true)} className="bg-white/5 text-white text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1.5 hover:bg-white/10 transition-colors border border-white/10">
              <PencilSimple size={14} /> Edit
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="delete-project-btn" className="bg-red-500/10 text-red-400 text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1.5 hover:bg-red-500/20 transition-colors border border-red-500/20">
                  <Trash size={14} /> Delete
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0F0F0F] border-white/10">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this project?</AlertDialogTitle>
                  <AlertDialogDescription className="text-white/60">
                    Are you sure you want to delete this project? This action cannot be undone. All associated tasks, deliverables, crew assignments and project-specific financial records will be permanently removed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-delete-project" className="bg-transparent border-white/10 hover:bg-white/5">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="confirm-delete-project" onClick={onDelete} className="bg-red-500 hover:bg-red-500/90 text-white">
                    Delete project
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        }
      />
      <div className="p-8 space-y-4 fade-up">
        {/* Overview KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="hairline rounded-lg p-4 bg-[#0F0F0F]">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Invoice</div>
            <div className="mono text-xl font-bold mt-1">{fmtINR(p.invoice_amount)}</div>
            <div className="text-[11px] text-emerald-400 mono mt-1">{fmtINR(p.amount_paid)} paid</div>
          </div>
          <div className="hairline rounded-lg p-4 bg-[#0F0F0F]">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Crew Cost</div>
            <div className="mono text-xl font-bold mt-1">{fmtINR(p.crew_cost)}</div>
          </div>
          <div className="hairline rounded-lg p-4 bg-[#0F0F0F]">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Expenses</div>
            <div className="mono text-xl font-bold mt-1">{fmtINR(p.expense_total)}</div>
          </div>
          <div className="hairline rounded-lg p-4 bg-[#0F0F0F]">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Profit</div>
            <div className={`mono text-xl font-bold mt-1 ${p.profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtINR(p.profit)}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="hairline rounded-lg bg-[#0F0F0F] p-5">
              <div className="text-sm font-semibold mb-3">Overview</div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Kv label="Status" value={p.status} />
                <Kv label="Payment" value={p.payment_status} />
                <Kv label="Shoot Date" value={fmtDate(p.shoot_date)} />
                <Kv label="Delivery" value={fmtDate(p.delivery_date)} />
                <Kv label="Call Time" value={p.call_time || "—"} />
                <Kv label="Location" value={p.location || "—"} />
              </div>
              {p.description && <p className="text-sm text-white/70 mt-4 border-t border-white/5 pt-3">{p.description}</p>}
            </div>

            <div className="hairline rounded-lg bg-[#0F0F0F]">
              <div className="p-4 hairline-b text-sm font-semibold">Crew ({p.crew.length})</div>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-widest text-white/40">
                  <tr className="hairline-b"><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Role</th><th className="text-right px-4 py-2">Payout</th><th className="text-right px-4 py-2">Travel</th><th className="text-right px-4 py-2">Equip</th><th className="text-right px-4 py-2">Total</th></tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {p.crew.map((c, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 font-medium">{c.freelancer_name}</td>
                      <td className="px-4 py-2 text-white/60">{c.role}</td>
                      <td className="px-4 py-2 text-right mono">{fmtINR(c.payout)}</td>
                      <td className="px-4 py-2 text-right mono">{fmtINR(c.travel)}</td>
                      <td className="px-4 py-2 text-right mono">{fmtINR(c.equipment_rental)}</td>
                      <td className="px-4 py-2 text-right mono font-bold">{fmtINR((c.payout||0)+(c.travel||0)+(c.equipment_rental||0))}</td>
                    </tr>
                  ))}
                  {p.crew.length === 0 && <tr><td colSpan="6" className="text-center text-white/40 py-6">No crew assigned.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="hairline rounded-lg bg-[#0F0F0F]">
              <div className="p-4 hairline-b text-sm font-semibold">Expenses</div>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-widest text-white/40">
                  <tr className="hairline-b"><th className="text-left px-4 py-2">Category</th><th className="text-left px-4 py-2">Description</th><th className="text-right px-4 py-2">Amount</th></tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {p.expenses.map((e, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 uppercase text-[10px] tracking-widest text-white/60">{e.category}</td>
                      <td className="px-4 py-2">{e.description}</td>
                      <td className="px-4 py-2 text-right mono">{fmtINR(e.amount)}</td>
                    </tr>
                  ))}
                  {p.expenses.length === 0 && <tr><td colSpan="3" className="text-center text-white/40 py-6">No expenses logged.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="hairline rounded-lg bg-[#0F0F0F]">
            <div className="p-4 hairline-b text-sm font-semibold">Deliverables ({p.deliverables.length})</div>
            <div className="p-4 space-y-3">
              {p.deliverables.map((d) => (
                <div key={d.id} className="hairline rounded-md p-3 bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    <Package size={14} weight="duotone" className="text-[#D97706]" />
                    <div className="text-sm font-medium truncate flex-1">{d.name}</div>
                    <span className="text-[9px] uppercase tracking-widest text-white/50">{d.type}</span>
                  </div>
                  <div className="text-[11px] text-white/50 mt-1">Editor · {d.editor_name || "—"}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-widest ${d.status === "done" ? "bg-emerald-500/10 text-emerald-400" : d.status === "in_progress" ? "bg-blue-500/10 text-blue-400" : "bg-white/10 text-white/60"}`}>{d.status}</span>
                    <span className="text-[11px] mono text-white/50">{fmtDate(d.deadline)}</span>
                  </div>
                </div>
              ))}
              {p.deliverables.length === 0 && <div className="text-white/40 text-sm py-4">No deliverables.</div>}
            </div>
          </div>
        </div>
      </div>
      <ProjectDialog open={open} onOpenChange={setOpen} onSaved={load} existing={p} />
    </>
  );
}

function Kv({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}
